import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Buildings, Package, CaretRight } from '@phosphor-icons/react';
import ListSearch from '../../../components/ListSearch';
import { SkeletonBlock, EmptyState } from '../../../components/module-ui';
import { listCompanies, listAssetTeams } from '../api';

/**
 * Top of the Assets hierarchy view: Companies, each showing how many
 * products it owns. Products with no company yet are grouped under a
 * virtual "Unassigned" card so nothing is hidden from this view.
 */
function CompaniesListPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    Promise.all([listCompanies(), listAssetTeams()])
      .then(([c, p]) => { setCompanies(c); setProducts(p); })
      .catch((e) => console.error('Failed to fetch companies:', e))
      .finally(() => setLoading(false));
  }, []);

  const unassignedCount = products.filter((p) => p.product_id && !p.company_id).length;

  const cards = [
    ...companies.map((c) => ({
      id: c.company_id,
      name: c.name,
      description: c.description,
      active: c.active,
      productCount: products.filter((p) => p.company_id === c.company_id).length,
    })),
    ...(unassignedCount > 0 ? [{
      id: 'unassigned',
      name: 'Unassigned',
      description: 'Products with no company assigned yet.',
      active: true,
      productCount: unassignedCount,
    }] : []),
  ];

  const q = query.trim().toLowerCase();
  const filtered = !q ? cards : cards.filter((c) => (c.name || '').toLowerCase().includes(q));

  return (
    <div data-testid="asset-companies-view">
      <ListSearch
        value={query}
        onChange={setQuery}
        placeholder="Search companies…"
        resultCount={filtered.length}
        totalCount={cards.length}
      />

      {loading ? (
        <SkeletonBlock />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Buildings}
          title="No companies found"
          description={query ? 'Try a different search.' : 'Add companies from Product Management.'}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
          {filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid="company-card"
              onClick={() => navigate(`/dashboard/assets/companies/${c.id}`)}
              style={{
                background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px',
                padding: '14px', textAlign: 'left', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', gap: '8px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <p style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>{c.name}</p>
                {!c.active && (
                  <span style={{ fontSize: '11px', color: '#9CA3AF' }}>inactive</span>
                )}
                <CaretRight size={14} color="#9CA3AF" style={{ marginLeft: 'auto' }} />
              </div>
              {c.description && (
                <p style={{ fontSize: '12px', color: '#6B7280' }}>{c.description}</p>
              )}
              <div style={{ display: 'flex', gap: '14px', marginTop: 'auto', fontSize: '12px', color: '#6B7280' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <Package size={13} /> {c.productCount} product{c.productCount === 1 ? '' : 's'}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default CompaniesListPage;
